import Grid from "@mui/material/Grid";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "custom/LayoutContainers/DashboardLayout";
import {useCallback, useEffect, useState} from "react";
import {
    createBranchAdmin,
    deleteBranchAdmin,
    getAllBranchAdmins,
    updateBranchAdmin
} from "../../api/branchadmin/branchAdminApiClient";
import {getCompanyById} from "../../api/company/companyApiClient";
import DeleteConfirmDialog from "./components/DeleteConfirmDialog";
import UserFormDialog from "../../components/UserFormDialog";
import {getAllBranches} from "../../api/branch/branchApiClient";
import SoftButton from "components/SoftButton";
import SoftInput from "../../components/SoftInput";
import {Outlet, useLocation, useNavigate} from "react-router-dom";
import {IconButton} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {Delete as DeleteIcon, Edit as EditIcon, Visibility as VisibilityIcon} from "@mui/icons-material";
import EnhancedTable from "../../components/EnhancedTable";
import DataTable, {renderBase64ImageCell, renderPill} from "../../components/DataTable";
import {getStatusColor, getStatusDisplayName, getUserRoleColor, getRoleDisplayName} from "../../utils/statusUtils";
import {handleApiError, showDeleteSuccess} from "../../utils/notificationUtils";
import ViewCompanyDialog from "../company/components/ViewCompanyDialog";

function Overview() {
    const [branchAdmins, setBranchAdmins] = useState([]);
    const [filteredBranchAdmins, setFilteredBranchAdmins] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [branchAdminToDelete, setBranchAdminToDelete] = useState(null);
    const [branchAdminToEdit, setBranchAdminToEdit] = useState(null);
    const [editAddDialogOpen, setEditAddDialogOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [companyToView, setCompanyToView] = useState(null);
    const [viewCompanyDialogOpen, setViewCompanyDialogOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const isOnChildRoute = location.pathname.includes('/branch-admins/') && location.pathname !== '/branch-admins';

    const columns = [
        {
            name: "profile Pic",
            value: "profilePic",
            align: "center",
            width: "10",
            renderCell: (row) => renderBase64ImageCell(row, "profilePic"),
        },
        {name: "first Name", value: "firstName", align: "left", width: "10"},
        {name: "Last Name", value: "lastName", align: "left", width: "10"},
        {name: "email", value: "email", align: "left", width: "20"},
        {name: "phone No", value: "phoneNumber", align: "left", width: "10"},
        {
            name: "Branch",
            value: "branchName",
            align: "left",
            width: "15",
            renderCell: (row) => (
                row.branchName && row.branchName !== "N/A" && row.branchId ? (
                    <SoftTypography
                        variant="button"
                        fontWeight="medium"
                        color="primary"
                        sx={{cursor: "pointer", "&:hover": {textDecoration: "underline"}}}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            navigate(`/branches/${row.branchId}`);
                        }}
                    >
                        {row.branchName}
                    </SoftTypography>
                ) : (
                    <SoftTypography variant="button" fontWeight="regular" color="text">
                        {row.branchName || "N/A"}
                    </SoftTypography>
                )
            ),
        },
        {
            name: "Company",
            value: "companyName",
            align: "left",
            width: "15",
            renderCell: (row) => (
                row.companyName && row.companyName !== "N/A" ? (
                    <SoftTypography
                        variant="button"
                        fontWeight="medium"
                        color="primary"
                        sx={{cursor: "pointer", "&:hover": {textDecoration: "underline"}}}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleCompanyNameClick(row);
                        }}
                    >
                        {row.companyName}
                    </SoftTypography>
                ) : (
                    <SoftTypography variant="button" fontWeight="regular" color="text">
                        {row.companyName || "N/A"}
                    </SoftTypography>
                )
            ),
        },
        {
            name: "position",
            value: "userRole",
            align: "center",
            width: "10",
            renderCell: (row) => renderPill(getRoleDisplayName(row.userRole), getUserRoleColor(row.userRole)),
        },
        {
            name: "status",
            value: "status",
            align: "center",
            width: "10",
            renderCell: (row) => renderPill(getStatusDisplayName(row.status), getStatusColor(row.status)),
        },
        {
            name: "Actions",
            value: "action",
            align: "center",
            width: "10",
            renderCell: (row) => (
                <>
                    <IconButton color="primary" size="small" title="View Details" onClick={() => handleViewClick(row)}>
                        <VisibilityIcon/>
                    </IconButton>
                    <IconButton color="primary" size="small" title="Edit" onClick={() => handleEditClick(row)}>
                        <EditIcon/>
                    </IconButton>
                    <IconButton color="error" size="small" title="Delete" onClick={() => handleDeleteClick(row)}>
                        <DeleteIcon/>
                    </IconButton>
                </>
            ),
        },
    ];

    const loadBranchAdmins = useCallback(async () => {
        try {
            setIsLoading(true);
            
            const response = await getAllBranchAdmins();
            // Transform data to include companyName and companyId
            // Backend now returns companyId and companyName directly from branch.company
            const transformedData = response.data.map(admin => {
                // Preserve companyId from all possible locations (backend now returns it directly)
                const companyId = admin.companyId 
                    || admin.branch?.company?.id 
                    || admin.branch?.companyId 
                    || admin.company?.id;
                
                // Get company name from all possible locations (backend now returns it directly)
                const companyName = admin.companyName 
                    || admin.branch?.company?.name 
                    || admin.company?.name 
                    || 'N/A';
                
                // Same defensive resolution for the branch. The API flattens it
                // onto the admin, but accept a nested shape too so the Branch
                // column does not silently fall back to "N/A".
                const branchId = admin.branchId || admin.branch?.id;
                const branchName = admin.branchName || admin.branch?.name || 'N/A';

                return {
                    ...admin,
                    companyName,
                    companyId, // Ensure companyId is always preserved in the data
                    branchName,
                    branchId
                };
            });
            setBranchAdmins(transformedData);
            setFilteredBranchAdmins(transformedData);
        } catch (error) {
            console.error('Error loading branch admins:', error);
            handleApiError(error, 'Failed to load branch admins. Please try again.');
            setBranchAdmins([]);
            setFilteredBranchAdmins([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadBranchAdmins().then();
    }, [loadBranchAdmins]);

    const handleBranchAdminAdded = async (formData) => {
        try {
            await createBranchAdmin(formData);
            await loadBranchAdmins();
        } catch (error) {
            throw error;
        }

    };

    const handleViewClick = (branchAdmin) => {
        navigate(`/branch-admins/${branchAdmin.id}`);
    };

    const handleDeleteClick = (branchAdmin) => {
        setBranchAdminToDelete(branchAdmin);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await deleteBranchAdmin(branchAdminToDelete.id);
            await loadBranchAdmins();
            setDeleteDialogOpen(false);
            setBranchAdminToDelete(null);
            showDeleteSuccess('Branch Admin');
        } catch (err) {
            handleApiError(err, 'Failed to delete branch admin');
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setBranchAdminToDelete(null);
    };

    const handleEditClick = (branchAdmin) => {
        setBranchAdminToEdit(branchAdmin);
        setIsEdit(true);
        setEditAddDialogOpen(true);
    };

    const handleEditBranchAdmin = async (formData) => {
        await updateBranchAdmin(branchAdminToEdit.id, formData);
        await loadBranchAdmins();
        setEditAddDialogOpen(false);
        setBranchAdminToEdit(null);
    };
    const handleOpenAddDialog = () => {
        setBranchAdminToEdit(null);
        setIsEdit(false);
        setEditAddDialogOpen(true);
    }
    const handleCloseDialog = () => {
        setDeleteDialogOpen(false)
        setEditAddDialogOpen(false);
        setBranchAdminToEdit(null);
        setBranchAdminToDelete(null);
        setViewCompanyDialogOpen(false);
        setCompanyToView(null);
    }

    const handleCompanyNameClick = async (branchAdmin) => {
        try {
            setIsLoading(true);
            // Check if company object exists, otherwise fetch using companyId
            if (branchAdmin.branch?.company) {
                setCompanyToView(branchAdmin.branch.company);
                setViewCompanyDialogOpen(true);
            } else if (branchAdmin.company) {
                setCompanyToView(branchAdmin.company);
                setViewCompanyDialogOpen(true);
            } else if (branchAdmin.companyId) {
                const response = await getCompanyById(branchAdmin.companyId);
                if (response && response.data) {
                    setCompanyToView(response.data);
                    setViewCompanyDialogOpen(true);
                } else {
                    console.warn("Company data not found for ID:", branchAdmin.companyId);
                    handleApiError(new Error("Company details not found."), "Failed to load company details.");
                }
            } else {
                console.warn("No company or companyId found for branch admin:", branchAdmin.id);
                handleApiError(new Error("Company details not available."), "Company details not available.");
            }
        } catch (error) {
            console.error('Error loading company details:', error);
            handleApiError(error, 'Failed to load company details. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleFilterBranchAdmin = (value) => {
        let filtered = branchAdmins.filter(branchAdmin => {
            return branchAdmin.firstName.toLowerCase().includes(value.toLowerCase()) ||
                branchAdmin.lastName.toLowerCase().includes(value.toLowerCase()) ||
                branchAdmin.email.toLowerCase().includes(value.toLowerCase()) ||
                branchAdmin.phoneNumber.toLowerCase().includes(value.toLowerCase());
        });
        setFilteredBranchAdmins(filtered);
    }

    const handleOnSubmit = async (formData) => {
        try {
            if (isEdit) {
                await handleEditBranchAdmin(formData);
            } else {
                await handleBranchAdminAdded(formData);
            }
            setEditAddDialogOpen(false);
        } catch (error) {
            console.error('Error in handleOnSubmit:', error);
            throw error;
        }
    };

    return (
        <DashboardLayout>
            <SoftBox pt={2} px={2} width="100%">
                <SoftBox pt={2} px={2} display="flex" alignItems="center" justifyContent="space-between" width="100%" gap={2}>
                    <SoftBox display="flex" alignItems="center" gap={2} flexShrink={0}>
                        {isOnChildRoute && (
                            <IconButton
                                onClick={() => navigate('/branch-admin')}
                                sx={{
                                    backgroundColor: 'primary.main',
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: 'primary.dark',
                                    }
                                }}
                            >
                                <ArrowBackIcon/>
                            </IconButton>
                        )}
                        <SoftTypography variant="h6" fontWeight="medium">
                            {isOnChildRoute ? 'Branch Admin Details' : 'Branch Admins'}
                        </SoftTypography>
                    </SoftBox>
                    {!isOnChildRoute && (
                        <>
                            <SoftBox flexGrow={1} maxWidth={560}>
                                <SoftInput
                                    placeholder="Type here to filter by name, email or phone"
                                    icon={{component: "search", direction: "left"}}
                                    onChange={(event) => handleFilterBranchAdmin(event.target.value)}
                                />
                            </SoftBox>
                            <SoftButton color="primary" onClick={handleOpenAddDialog} sx={{flexShrink: 0}}>
                                Add Branch Admin
                            </SoftButton>
                        </>
                    )}
                </SoftBox>
            </SoftBox>
            <SoftBox p={2} width="100%">
                {isOnChildRoute ? (
                    <Outlet/>
                ) : (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <EnhancedTable
                                TableComponent={DataTable}
                                isLoading={isLoading}
                                data={filteredBranchAdmins}
                                loadingMessage="Loading branch admins..."
                                noDataMessage="No branch admins found"
                                columns={columns}
                            />
                        </Grid>
                    </Grid>
                )}
            </SoftBox>
            <DeleteConfirmDialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                onDelete={handleDeleteConfirm}
                warningMessage={`Are you sure you want to delete ${branchAdminToDelete?.firstName || ""} ${branchAdminToDelete?.lastName || ""}? This action cannot be undone.`}
            />
            <UserFormDialog
                open={editAddDialogOpen}
                onClose={handleCloseDialog}
                onSubmit={handleOnSubmit}
                user={branchAdminToEdit}
                isEdit={isEdit}
                entityLabel="Branch Admin"
                defaultUserRole="BRANCH_ADMIN"
                minAge={18}
                extraFields={[{
                    key: "branchId",
                    label: "Branch",
                    type: "asyncSelect",
                    loader: getAllBranches,
                    getValue: (b) => b.branchId,
                    getLabel: (b) => b.location,
                    required: true,
                    span: 12,
                }]}
            />
            <ViewCompanyDialog
                open={viewCompanyDialogOpen}
                onClose={() => {
                    setViewCompanyDialogOpen(false);
                    setCompanyToView(null);
                }}
                company={companyToView}
            />
        </DashboardLayout>
    );
}

export default Overview;
